/**
 * ModuleSelector — switch between functional domains when more than one is accessible.
 *
 * Available and read-only routes from the backend navigation contract are
 * grouped into functional domains. Selecting a domain navigates to its first
 * authorized route; client-side role inference is never used.
 */
import { useState, useMemo, useRef } from 'react';
import { Box, Chip, Menu, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import AppsIcon from '@mui/icons-material/Apps';
import type { IconId, TranslationKey } from '@finance/contracts';
import { MODULE_TO_DOMAIN, DOMAIN_LABEL_KEYS } from '@finance/contracts';
import { useBackendNavigation } from '../../runtime/navigation/use-navigation';
import { getRouteById } from '../../routes/registry';
import { resolveIcon } from '../../runtime/icons';
import { resolveTKey } from '../../i18n/resolve-key';

interface DomainOption {
  domain: string;
  label: string;
  routePath: string;
  iconKey?: string;
}

export function ModuleSelector() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: contract } = useBackendNavigation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const domains = useMemo<DomainOption[]>(() => {
    const authorized = [...(contract?.primary ?? []), ...(contract?.secondary ?? [])].filter(
      (item) => item.availability === 'AVAILABLE' || item.availability === 'READ_ONLY',
    );
    const seen = new Set<string>();
    const result: DomainOption[] = [];
    for (const item of authorized) {
      const domain = MODULE_TO_DOMAIN[item.module];
      if (!domain || seen.has(domain)) continue;
      const route = getRouteById(item.routeId);
      if (!route) continue;
      seen.add(domain);
      const labelKey = DOMAIN_LABEL_KEYS[domain] ?? ('shell:moduleSelector.fallback' as TranslationKey);
      result.push({
        domain,
        label: t(resolveTKey(labelKey)),
        routePath: route.path,
        iconKey: item.iconKey ?? route.iconId,
      });
    }
    return result;
  }, [contract, t]);

  const activeDomain = useMemo(() => domains.find((domain) =>
    location.pathname === domain.routePath || location.pathname.startsWith(`${domain.routePath}/`),
  ), [domains, location.pathname]);

  if (domains.length <= 1) return null;

  const displayLabel = activeDomain?.label ?? domains[0]?.label ?? t('shell:moduleSelector.fallback');
  const ActiveIcon = resolveIcon(activeDomain?.iconKey as IconId | undefined);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Chip
        icon={ActiveIcon ? <ActiveIcon fontSize="small" /> : <AppsIcon fontSize="small" />}
        label={displayLabel}
        onClick={(event) => {
            triggerRef.current = event.currentTarget as HTMLElement;
            setAnchorEl(event.currentTarget);
          }}
        variant="outlined"
        sx={{
          color: 'inherit',
          maxWidth: { md: 140, lg: 180 },
          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        }}
        data-testid="module-selector"
        aria-label={t('shell:moduleSelector.ariaLabel')}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
            const focusTarget = anchorEl;
            setAnchorEl(null);
            window.requestAnimationFrame(() => (focusTarget as HTMLElement | null)?.focus());
          }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        data-testid="module-selector-menu"
      >
        {domains.map((option) => {
          const Icon = resolveIcon(option.iconKey as IconId | undefined);
          const isActive = option.domain === activeDomain?.domain;
          return (
            <MenuItem
              key={option.domain}
              selected={isActive}
              onClick={() => { setAnchorEl(null); navigate(option.routePath); }}
              data-testid="module-selector-item"
              data-value={option.domain}
            >
              {Icon && <Box component={Icon} sx={{ mr: 1, fontSize: '1.25rem' }} />}
              <Typography noWrap>{option.label}</Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
